import { Button, Card, Input, message, Tabs, Table, Typography, Skeleton } from "antd";
import { useEffect, useState } from "react";
import AppButton from "../../common/AppButton";
import DocumentModal from "../../components/DocumentModal";
import DocumentService from "../../services/Document/DocumentService";
import {
    faEye,
    faPenToSquare,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";
import { faArrowUpFromBracket } from "@fortawesome/pro-solid-svg-icons";
import dayjs, { fromUtc } from "../../helpers/dayjsSetup";
import { faTrashCan } from "@fortawesome/pro-duotone-svg-icons/faTrashCan";
import ImportPoModal from "../../components/ImportPoModal";

const { Text } = Typography;

const PurchaseOrderDocumentStatusPage = ({
    purchaseOrderId,
    currentPo,
    purchaseOrderLineItems,
    handlePoLineItemChange,
    handleAddNewPoLineItem,
    handlePoLineItemDelete,
    onImportPurchaseOrderLineItems }) => {
    const [activeKey, setActiveKey] = useState("items");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isImportPoModalOpen, setIsImportPoModalOpen] = useState(false);
    const [selectedDocRow, setSelectedDocRow] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [docList, setDocList] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleSelectedDocument = (row, index) => {
        setSelectedIndex(index);
        setSelectedDocRow(row);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedDocRow(null);
        setSelectedIndex(null);
    };

    const uploadModal = () => {
        setIsModalOpen(true);
        setSelectedDocRow({ ...selectedDocRow, entityType: "PurchaseOrder" });
    };

    const handlePreview = (record) => {
        setSelectedDocument(record);
        setIsModalVisible(true);
    };

    const handleChange = (index, field, value) => {
        const updated = [...docList];
        updated[index][field] = value;
        setDocList(updated);
    };

    const arranged = (arr) => {
        if (!Array.isArray(arr)) return [];

        const filtered = arr
            .filter(
                (doc) =>
                    doc.documentType?.isMandatory === true || typeof doc.id === "string",
            )
            .map((doc, index) => ({ ...doc, key: index })); // Added a unique key for each item

        const sorted = filtered.sort(
            (a, b) =>
                (b.documentType?.isMandatory === true) -
                (a.documentType?.isMandatory === true),
        );
        return sorted;
    };

    const handleOpenImportPoModal = () => {
        setIsImportPoModalOpen(true);
    }

    const poLineItemColumns = [
        {
            title: "SKU",
            dataIndex: "sku",
            width: 170,
            render: (text, row, index) =>
                <Input
                    value={text}
                    onChange={(e) => handlePoLineItemChange(index, "sku", e.target.value)}
                    className="text-sm"
                />
        },
        {
            title: "Description",
            dataIndex: "description",
            width: 300,
            render: (text, row, index) => (
                <Input.TextArea
                    value={text}
                    onChange={(e) => handlePoLineItemChange(index, "description", e.target.value)}
                    className="text-sm"
                    autoSize={{ minRows: 2, maxRows: 2 }}
                    style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                    }}
                />
            )
        },
        {
            title: "PO Quantity",
            dataIndex: "poQty",
            render: (text, row, index) =>
                <Input
                    value={text}
                    onChange={(e) => handlePoLineItemChange(index, "poQty", e.target.value)}
                    className="text-sm w-24"
                    type="number"
                    min={0}
                />
        },
        {
            title: "Supply Quantity",
            dataIndex: "supplyQty",
            render: (text, row, index) =>
                <Input
                    value={text}
                    onChange={(e) => handlePoLineItemChange(index, "supplyQty", e.target.value)}
                    className="text-sm w-24"
                    type="number"
                    min={0}
                />
        },
        {
            title: "Unit Cost",
            dataIndex: "unitCost",
            render: (text, row, index) =>
                <Input
                    value={text}
                    onChange={(e) => handlePoLineItemChange(index, "unitCost", e.target.value)}
                    className="text-sm w-24"
                    type="number"
                    min={0}
                />
        },
        {
            title: "Value",
            render: (value, row, index) => {
                return (
                    <span>{(value.poQty * value.unitCost).toFixed(2)}</span>
                );
            },
        },
        {
            title: "Container Number",
            render: () => {
                return (
                    <span></span>
                );
            },
        },
        {
            title: "ETA",
            render: () => {
                return (
                    <span></span>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (value, row, index) => {
                return (
                    <AppButton variant="delete"
                        onClick={() => handlePoLineItemDelete(value)}
                    >
                    </AppButton>

                );
            },
        },
    ];

    const columns = [
        {
            title: "",
            key: "preview",
            align: "center",
            render: (row) => (
                <Button
                    type="link"
                    // onClick={() => handleSelectedDocument(row, index)}
                    onClick={() => handlePreview(row)}
                >
                    {/* default */}
                    <FontAwesomeIcon
                        icon={faEye}
                        className="text-2xl text-black block p-2 hover:text-cyan-700"
                    />
                </Button>
            ),
        },
        {
            title: <span className="ml-10">Type</span>,
            // dataIndex: ["documentType", "type"],
            render: (_, record) => {
                const isMandatory = record.documentType?.isMandatory;
                const hasUser = !!record.user;
                return (
                    <span className="flex items-center justify-start gap-4">
                        {isMandatory && hasUser && (
                            <span className="w-5 h-5 rounded-full bg-green-500 opacity-75" />
                        )}
                        {isMandatory && !hasUser && (
                            <span className="w-5 h-5 rounded-full bg-red-500 opacity-75" />
                        )}
                        {!isMandatory && (
                            <span className="w-5 h-5" /> // empty space
                        )}
                        <span className="text-sm">{record.documentType?.type}</span>
                    </span>
                );
            },
        },
        {
            title: "File Name",
            dataIndex: "originalFileName",
            width: 600,
            render: (text, record, index) => {
                const handleDownload = async () => {
                    try {
                        const response = await DocumentService.handleDownload(record?.id);
                        const contentDisposition = response.headers["content-disposition"];
                        let fileName = response.fileName || "download";
                        if (contentDisposition) {
                            const utf8Match = contentDisposition.match(
                                /filename\*\=UTF-8''([^;]+)/,
                            );
                            if (utf8Match) {
                                fileName = decodeURIComponent(utf8Match[1]);
                            }
                        }

                        const blob = new Blob([response.data]);
                        const blobUrl = window.URL.createObjectURL(blob);

                        const link = document.createElement("a");
                        link.href = blobUrl;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    } catch (error) {
                        if (error?.status === 404) {
                            message.error("Document Not Found");
                        } else {
                            message.error(
                                "Something went wrong while downloading the document",
                            );
                        }
                        console.error("Document Download Failed. ", error);
                    }
                };

                return record.id ? (
                    <div className="grid">
                        <a
                            onClick={handleDownload}
                            className="p-0 cursor-pointer text-black text-sm"
                        >
                            {record.document?.OriginalFileName || record.originalFileName}
                        </a>
                        <span className="text-sm">
                            uploaded on {fromUtc(record.createdDate).format("DD MMM YYYY")}
                        </span>
                    </div>
                ) : (
                    <Text
                        type="link"
                        onClick={() => handleSelectedDocument(record, index)}
                        className="hover:cursor-pointer flex items-center justify-start w-full text-[#015373] text-sm"
                    >
                        Upload
                        <FontAwesomeIcon icon={faArrowUpFromBracket} className="ml-2" />
                    </Text>
                );
            },
        },

        {
            title: "Note",
            dataIndex: "notes",
            width: 250,
            render: (text, row, index) =>
                row.isEditing ? (
                    <Input
                        value={text}
                        onChange={(e) => handleChange(index, "notes", e.target.value)}
                        className="text-sm"
                    />
                ) : text ? (
                    text.length > 20 ? (
                        `${text.substring(0, 20)}...`
                    ) : (
                        text
                    )
                ) : (
                    "-"
                ),
        },
        {
            title: "Uploaded By",
            dataIndex: "user",
            render: (user) => (
                <span className="text-sm">
                    {user?.firstName ? user.firstName + " " + user.lastName : "-"}
                </span>
            ),
        },
        {
            title: "Action",
            key: "acrion",
            align: "center",
            render: (value, row, index) => {
                const isSelected = selectedIndex === index;
                return (
                    <Button
                        type="link"
                        onClick={() => handleSelectedDocument(row, index)}
                    >
                        {isSelected ? (
                            <FontAwesomeIcon
                                icon={faPenToSquare}
                                className="text-xl block pl-3 p-2 text-cyan-700 bg-neutral-200 rounded-3xl"
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={faPenToSquare}
                                className="text-xl text-black block pl-3 p-2 hover:text-cyan-500"
                            />
                        )}
                    </Button>
                );
            },
        },
    ];

    const statusColumns = [
        { title: "Status", dataIndex: "status" },
        {
            title: "Event Date",
            dataIndex: "eventDate",
            render: (text) =>
                loading ? (
                    <Skeleton active />
                ) : (
                    <span className="text-sm">
                        {dayjs(text).format("DD MMM YYYY HH:mm")}
                    </span>
                ),
        },
        {
            title: "Update Date",
            dataIndex: "createdDate",
            render: (text) => (
                <span className="text-sm">
                    {fromUtc(text).format("DD MMM YYYY HH:mm")}
                </span>
            ),
        },
        { title: "Source", dataIndex: "trackingType" },
    ];

    return (
        <>
            <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 mt-2">
                <Tabs
                    type="card"
                    activeKey={activeKey}
                    onChange={setActiveKey}
                    tabBarExtraContent={
                        activeKey === "document" ? (
                            <AppButton variant="new" onClick={uploadModal}>
                                New
                            </AppButton>
                        ) : activeKey === "items" ? (
                            <>
                                <AppButton variant="new" onClick={handleAddNewPoLineItem}>
                                    New
                                </AppButton>
                                <AppButton variant="import" onClick={handleOpenImportPoModal}>
                                    Import
                                </AppButton>
                            </>
                        ) : null
                    }
                >
                    <Tabs.TabPane tab="Items" key="items">
                        <div className="text-center text-slate-500">
                            <Table
                                columns={poLineItemColumns}
                                dataSource={purchaseOrderLineItems}
                                pagination={false}
                                rowKey="key"
                                className="border rounded"
                                scroll={{ x: "max-content" }} // or a fixed px like 1000
                                rowClassName={(_, index) =>
                                    `text-sm ${index % 2 === 0 ? "" : "bg-[#f3f3f3]"}`
                                }
                                size="middle"
                            />
                        </div>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Document" key="document">
                        <div className="text-center text-slate-500">
                            <Table
                                columns={columns}
                                dataSource={arranged(currentPo?.documents)}
                                pagination={false}
                                rowKey="key"
                                className="border rounded"
                                scroll={{ x: "max-content" }} // or a fixed px like 1000
                                rowClassName={(_, index) =>
                                    `text-sm ${index % 2 === 0 ? "" : "bg-[#f3f3f3]"}`
                                }
                                size="middle"
                            />
                        </div>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Status History" key="status-history">
                        <Table
                            columns={statusColumns}
                            dataSource={currentPo?.purchaseOrderHistory}
                            pagination={false}
                            rowKey="id"
                            className="border rounded"
                            rowClassName={(_, index) =>
                                `text-sm ${index % 2 === 0 ? "" : "bg-[#f3f3f3]"}`
                            }
                            size="middle"
                        />
                    </Tabs.TabPane>
                </Tabs>
            </Card>

            <DocumentModal
                isOpen={isModalOpen}
                entityID={purchaseOrderId}
                onClose={() => handleCloseModal()}
                selectedDocumentTypeID={selectedDocRow?.documentType?.id}
                initialValues={selectedDocRow}
                onSubmit={() => {
                    setIsModalOpen(false);
                }}
            />
            <ImportPoModal
                isOpen={isImportPoModalOpen}
                entityID={purchaseOrderId}
                onClose={() => setIsImportPoModalOpen(false)}
                selectedDocumentTypeID={selectedDocRow?.documentType?.id}
                initialValues={selectedDocRow}
                onSubmit={() => {
                    setIsImportPoModalOpen(false);
                }}
                onImportPurchaseOrderLineItems={onImportPurchaseOrderLineItems}
            />
            <DocumentPreviewModal
                isOpen={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                document={selectedDocument}
            />
        </>
    );
};

export default PurchaseOrderDocumentStatusPage;